(() => {
	/**
	 * @typedef {Object} VariantLike
	 * @property {number|string} id
	 * @property {boolean} available
	 * @property {string|null} [option1]
	 * @property {string|null} [option2]
	 * @property {string|null} [option3]
	 * @property {number} [price]
	 */

	/**
	 * @typedef {Object} SelectedOption
	 * @property {'option1'|'option2'|'option3'} index
	 * @property {string|null} value
	 */

	/**
	 * @typedef {Object} AvailabilityEntry
	 * @property {string} value
	 * @property {boolean} soldOut
	 */

	const stickyRoots = Array.from(document.querySelectorAll('product-add-to-cart-sticky[data-section]'));
	if (!stickyRoots.length) return;

	const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();
	const isMobileMode = () => window.matchMedia('(max-width: 767px)').matches;

	/**
	 * Parses an inline JSON script inside a specific container.
	 * Returns null for missing or invalid JSON to keep initialization resilient.
	 *
	 * @param {ParentNode} container
	 * @param {string} selector
	 * @returns {any|null}
	 */
	const parseJsonScript = (container, selector) => {
		const script = container.querySelector(selector);
		if (!script) return null;
		try {
			return JSON.parse(script.textContent || 'null');
		} catch (error) {
			return null;
		}
	};

	/**
	 * Re-scopes variant inputs to sticky-specific selectors so sticky logic
	 * does not collide with the main PDP variant handlers.
	 *
	 * @param {HTMLElement} sticky
	 */
	const removeVariantInputBinding = (sticky) => {
		sticky.querySelectorAll('[data-variant-input]').forEach((element) => {
			element.setAttribute('data-sticky-option', element.getAttribute('data-index') || '');
			element.removeAttribute('data-variant-input');
		});
	};

	/**
	 * Boots one sticky ATC instance and binds all state, events and rendering.
	 *
	 * @param {HTMLElement} sticky
	 */
	const initSticky = (sticky) => {
		if (!sticky || sticky.dataset.stickyReady === 'true') return;
		sticky.dataset.stickyReady = 'true';
		removeVariantInputBinding(sticky);

		const variants = parseJsonScript(sticky, 'script[data-sticky-variants]') || [];
		if (!Array.isArray(variants) || !variants.length) return;
		const familyVariants = parseJsonScript(sticky, 'script[data-sticky-family-variants]') || [];

		const stickyFormId = sticky.dataset.stickyFormId || '';
		const stickyForm = stickyFormId ? document.getElementById(stickyFormId) : sticky.querySelector('form.sticky-product-form');
		const idInput = stickyForm ? stickyForm.querySelector('[data-sticky-variant-id]') : null;
		const atcBtn = sticky.querySelector('[data-sticky-atc-button]');
		const atcBtnText = atcBtn ? atcBtn.querySelector('.single-add-to-cart-button--text') : null;
		const atcBtnPrice = atcBtn ? atcBtn.querySelector('[data-sticky-mobile-price]') : null;
		const mobileCloseBtn = sticky.querySelector('[data-sticky-mobile-close]');
		const mobileToggleBtn = sticky.querySelector('[data-sticky-mobile-toggle]');
		const mobileToggleLabel = sticky.querySelector('[data-sticky-mobile-toggle-label]');
		const colorDisplay = sticky.querySelector('[data-sticky-color-display]');
		const colorDisplayDot = sticky.querySelector('[data-sticky-color-dot]');
		const colorDisplayName = sticky.querySelector('[data-sticky-color-name]');
		const mobileColorInfo = sticky.querySelector('[data-sticky-mobile-color-info]');
		const mobileColorDot = sticky.querySelector('[data-sticky-mobile-color-dot]');
		const mobileColorName = sticky.querySelector('[data-sticky-mobile-color-name]');

		const txtAdd = sticky.dataset.addLabel || 'Add to Cart';
		const txtSold = sticky.dataset.soldLabel || 'Sold out';
		const txtNotify = sticky.dataset.notifyLabel || 'Notify Me When Back in Stock';
		const txtSelectSize = sticky.dataset.selectSizeLabel || 'Add to Cart';
		const txtChangeSize = sticky.dataset.changeLabel || 'CHANGE SIZE';
		const txtAddBag = 'Add to Bag';

		const customDropdowns = [];
		const mobileOptionGroups = [];
		const mobileListSelectMap = new WeakMap();
		let selectedFamilyColor = null;
		let latestMainVariant = null;
		let stickyInteractionUntil = 0;
		const sectionRoot = sticky.closest('[id^="ProductSection-"]');
		const mainAtcBtn = sectionRoot ? sectionRoot.querySelector('.product-single__form button.add-to-cart') : null;
		const footerEls = document.querySelectorAll('.site-footer');
		const footerEl = footerEls.length ? footerEls[footerEls.length - 1] : null;

		const closeCustomDropdowns = () => {
			customDropdowns.forEach(({ wrapper, trigger }) => {
				wrapper.classList.remove('is-open');
				trigger.setAttribute('aria-expanded', 'false');
			});
		};

		const updateColorDisplay = (label, imageUrl) => {
			const applyToElement = (dotEl, nameEl) => {
				if (!dotEl && !nameEl) return;
				if (nameEl) nameEl.textContent = label || '';
				if (dotEl) {
					if (imageUrl) {
						dotEl.style.backgroundImage = `url(${imageUrl})`;
						dotEl.style.backgroundSize = 'cover';
						dotEl.style.backgroundPosition = 'center';
						dotEl.style.removeProperty('--dot-color');
					} else {
						dotEl.style.backgroundImage = '';
						dotEl.style.setProperty('--dot-color', (label || '').toLowerCase());
					}
				}
			};
			applyToElement(colorDisplayDot, colorDisplayName);
			applyToElement(mobileColorDot, mobileColorName);
		};

		const isMobileExpanded = () => sticky.classList.contains('is-mobile-expanded');
		const setMobileExpanded = (expanded) => {
			sticky.classList.toggle('is-mobile-expanded', expanded);
		};

		/**
		 * Formats a variant price using the theme money formatter.
		 * Returns empty string when no price is available.
		 *
		 * @param {VariantLike|null} variant
		 * @returns {string}
		 */
		const formatVariantPrice = (variant) => {
			if (!variant || variant.price == null) return '';
			if (window.theme && theme.Currency && theme.Currency.formatMoney) {
				return theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
			}
			return '';
		};

		/**
		 * Finds a variant in the current product's variant payload by id.
		 *
		 * @param {string|number|null|undefined} variantId
		 * @returns {VariantLike|null}
		 */
		const getVariantById = (variantId) => {
			const id = String(variantId || '');
			return variants.find((variant) => String(variant.id) === id) || null;
		};

		/**
		 * Resolves main PDP option selections from visible controls.
		 * Supports select-based pickers and radio-based pickers.
		 *
		 * @returns {SelectedOption[]}
		 */
		const getMainSelectedOptions = () => {
			if (!sectionRoot) return [];
			const selections = ['option1', 'option2', 'option3'].map((optionKey) => {
				const select = sectionRoot.querySelector(`select[data-variant-input][data-index="${optionKey}"], .variant-input-wrap[data-index="${optionKey}"] select[data-variant-input]`);
				if (select && select.value !== '') {
					return { index: optionKey, value: select.value };
				}

				const checkedInput = sectionRoot.querySelector(`input[data-variant-input][data-index="${optionKey}"]:checked, .variant-input-wrap[data-index="${optionKey}"] input[data-variant-input]:checked`);
				if (checkedInput && checkedInput.value !== '') {
					return { index: optionKey, value: checkedInput.value };
				}

				return null;
			}).filter(Boolean);

			const hasOption1 = selections.some((selection) => selection.index === 'option1');
			const appliedFamilyColor = getAppliedFamilyColor();
			if (!hasOption1 && appliedFamilyColor) {
				selections.unshift({ index: 'option1', value: appliedFamilyColor });
			}

			return selections;
		};

		/**
		 * Resolves the currently selected main PDP variant.
		 * Prefers latest `variantChange` payload and falls back to the form id input.
		 *
		 * @returns {VariantLike|null}
		 */
		const getMainSelectedVariant = () => {
			if (latestMainVariant) return latestMainVariant;
			if (!sectionRoot) return null;
			const mainIdInput = sectionRoot.querySelector('.product-single__form [name="id"]');
			const variantById = mainIdInput ? getVariantById(mainIdInput.value) : null;
			if (variantById) return variantById;

			const selected = getMainSelectedOptions();
			if (!selected.length) return null;

			const availableMatch = variants.find((variant) => {
				return !!variant.available && selected.every((selection) => {
					return normalizeValue(variant[selection.index]) === normalizeValue(selection.value);
				});
			}) || null;
			if (availableMatch) return availableMatch;

			return variants.find((variant) => {
				return selected.every((selection) => normalizeValue(variant[selection.index]) === normalizeValue(selection.value));
			}) || null;
		};

		/**
		 * Pushes a sticky option selection back to the main PDP controls.
		 * Supports both dropdown and radio-button variant pickers.
		 *
		 * @param {string|null} optionIndex
		 * @param {string|null} optionValue
		 */
		const syncMainOptionFromSticky = (optionIndex, optionValue) => {
			if (!sectionRoot || !optionIndex || !optionValue) return;

			const mainSelect = sectionRoot.querySelector(`select[data-variant-input][data-index="${optionIndex}"], .variant-input-wrap[data-index="${optionIndex}"] select[data-variant-input]`);
			if (mainSelect) {
				const mainMatchingOption = Array.from(mainSelect.options).find((option) => option.value === optionValue);
				if (!mainMatchingOption || mainSelect.value === mainMatchingOption.value) return;
				mainSelect.value = mainMatchingOption.value;
				mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
				return;
			}

			const mainRadio = sectionRoot.querySelector(`input[data-variant-input][data-index="${optionIndex}"][value="${CSS.escape(optionValue)}"], .variant-input-wrap[data-index="${optionIndex}"] input[data-variant-input][value="${CSS.escape(optionValue)}"]`);
			if (!mainRadio || mainRadio.checked) return;

			mainRadio.click();
			if (!mainRadio.checked) {
				mainRadio.checked = true;
				mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
			}
		};

		/**
		 * Validates selected family color against available variant datasets.
		 * Ensures we only constrain by a family color that actually exists.
		 *
		 * @returns {string|null}
		 */
		const getAppliedFamilyColor = () => {
			if (!selectedFamilyColor) return null;
			const selectedNorm = normalizeValue(selectedFamilyColor);
			const existsInFamilyVariants = familyVariants.some((variant) => normalizeValue(variant.option1) === selectedNorm);
			if (existsInFamilyVariants) return selectedFamilyColor;
			const existsInCurrentVariants = variants.some((variant) => normalizeValue(variant.option1) === selectedNorm);
			return existsInCurrentVariants ? selectedFamilyColor : null;
		};

		/**
		 * Returns the variant list used for matching and availability checks.
		 * Switches to family-level variants when family color context is valid.
		 *
		 * @returns {VariantLike[]}
		 */
		const getEffectiveVariants = () => {
			const appliedFamilyColor = getAppliedFamilyColor();
			if (!appliedFamilyColor) return variants;
			const colorNorm = normalizeValue(appliedFamilyColor);
			const byFamilyColor = familyVariants.filter((variant) => normalizeValue(variant.option1) === colorNorm);
			if (byFamilyColor.length) return byFamilyColor;
			return variants;
		};

		/**
		 * Determines whether this select behaves as a color selector by checking
		 * swatch metadata attributes on its options.
		 *
		 * @param {HTMLSelectElement} selectElement
		 * @returns {boolean}
		 */
		const isColorOption = (selectElement) => Array.from(selectElement.options).some((option) => {
			return !!(option.dataset && (option.dataset.swatchColor || option.dataset.swatchImage));
		});

		/**
		 * Builds a custom dropdown UI synced with a native select.
		 * Native select remains source of truth for availability and selected value.
		 *
		 * @param {HTMLSelectElement} selectElement
		 * @returns {{
		 * 	select: HTMLSelectElement,
		 * 	wrapper: HTMLDivElement,
		 * 	trigger: HTMLButtonElement,
		 * 	sync: () => void
		 * }}
		 */
		const buildCustomDropdown = (selectElement) => {
			selectElement.classList.add('sticky-native-select');
			const colorOption = isColorOption(selectElement);
			const wrapper = document.createElement('div');
			wrapper.className = 'sticky-select';

			const trigger = document.createElement('button');
			trigger.type = 'button';
			trigger.className = 'sticky-select__trigger';
			trigger.setAttribute('aria-expanded', 'false');

			const triggerValue = document.createElement('span');
			triggerValue.className = 'sticky-select__value';
			const chevron = document.createElement('span');
			chevron.className = 'sticky-select__chevron';
			trigger.append(triggerValue, chevron);

			const menu = document.createElement('div');
			menu.className = 'sticky-select__menu';

			const setValueUI = (optionElement) => {
				triggerValue.innerHTML = '';
				if (colorOption && optionElement.value) {
					const dot = document.createElement('span');
					dot.className = 'sticky-select__dot';
					if (optionElement.dataset.swatchImage) {
						dot.style.backgroundImage = `url(${optionElement.dataset.swatchImage})`;
						dot.style.backgroundSize = 'cover';
						dot.style.backgroundPosition = 'center';
					} else {
						dot.style.backgroundImage = '';
						dot.style.setProperty('--dot-color', optionElement.dataset.swatchColor || optionElement.value.toLowerCase());
					}
					triggerValue.appendChild(dot);
				}
				const text = document.createElement('span');
				text.textContent = optionElement.textContent || '';
				triggerValue.appendChild(text);
			};

			const syncOptionsFromSelect = () => {
				menu.querySelectorAll('.sticky-select__option').forEach((button) => {
					const value = button.getAttribute('data-value');
					const matching = Array.from(selectElement.options).find((option) => option.value === value);
					if (!matching) return;
					button.classList.toggle('is-selected', selectElement.value === value);
					button.classList.toggle('is-disabled', !!matching.disabled);
					button.disabled = false;
				});
				const current = selectElement.options[selectElement.selectedIndex] || selectElement.options[0];
				if (current) setValueUI(current);
			};

			Array.from(selectElement.options).forEach((optionElement) => {
				if (optionElement.value === '') return;
				const optionBtn = document.createElement('button');
				optionBtn.type = 'button';
				optionBtn.className = 'sticky-select__option';
				optionBtn.setAttribute('data-value', optionElement.value);

				if (colorOption && optionElement.value) {
					const dot = document.createElement('span');
					dot.className = 'sticky-select__dot';
					if (optionElement.dataset.swatchImage) {
						dot.style.backgroundImage = `url(${optionElement.dataset.swatchImage})`;
						dot.style.backgroundSize = 'cover';
						dot.style.backgroundPosition = 'center';
					} else {
						dot.style.setProperty('--dot-color', optionElement.dataset.swatchColor || optionElement.value.toLowerCase());
					}
					optionBtn.appendChild(dot);
				}

				const text = document.createElement('span');
				text.textContent = optionElement.textContent || '';
				optionBtn.appendChild(text);

				optionBtn.addEventListener('click', () => {
					optionElement.disabled = false;
					selectElement.value = optionElement.value;
					syncMainOptionFromSticky(selectElement.getAttribute('data-index') || null, optionElement.value);
					applyAvailability();
					updateState();
					wrapper.classList.remove('is-open');
					trigger.setAttribute('aria-expanded', 'false');
				});

				menu.appendChild(optionBtn);
			});

			trigger.addEventListener('click', () => {
				const nextOpen = !wrapper.classList.contains('is-open');
				closeCustomDropdowns();
				wrapper.classList.toggle('is-open', nextOpen);
				trigger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
			});

			selectElement.addEventListener('change', syncOptionsFromSelect);

			wrapper.append(trigger, menu);
			selectElement.parentNode.appendChild(wrapper);
			syncOptionsFromSelect();

			return { select: selectElement, wrapper, trigger, sync: syncOptionsFromSelect };
		};

		/**
		 * Builds mobile option buttons synced with a native select.
		 *
		 * @param {HTMLSelectElement} selectElement
		 * @returns {{ sync: () => void }|null}
		 */
		const buildMobileOptionGroup = (selectElement) => {
			const variantWrapper = selectElement.closest('.variant-wrapper--dropdown');
			if (!variantWrapper) return null;

			const labelElement = variantWrapper.querySelector('.variant__label');
			const labelText = (labelElement ? labelElement.textContent : '').trim() || 'Option';
			const optionGroup = document.createElement('div');
			optionGroup.className = 'sticky-mobile-option-group';

			const title = document.createElement('div');
			title.className = 'sticky-mobile-option-title';
			title.textContent = `${labelText}:`;
			const currentValue = document.createElement('span');
			currentValue.className = 'sticky-mobile-option-current';
			title.appendChild(currentValue);

			const list = document.createElement('div');
			list.className = 'sticky-mobile-option-list';
			list.setAttribute('data-sticky-mobile-select-index', selectElement.getAttribute('data-index') || '');
			mobileListSelectMap.set(list, selectElement);

			const colorOption = isColorOption(selectElement);
			Array.from(selectElement.options).forEach((optionElement) => {
				if (optionElement.value === '') return;
				const button = document.createElement('button');
				button.type = 'button';
				button.className = `sticky-mobile-option-button${colorOption ? ' sticky-mobile-option-button--color' : ''}`;
				button.setAttribute('data-value', optionElement.value);
				button.setAttribute('data-sticky-mobile-option-value', optionElement.value);

				if (colorOption) {
					const dot = document.createElement('span');
					dot.className = 'sticky-mobile-option-dot';
					if (optionElement.dataset.swatchImage) {
						dot.style.backgroundImage = `url(${optionElement.dataset.swatchImage})`;
					} else {
						dot.style.setProperty('--dot-color', optionElement.dataset.swatchColor || optionElement.value.toLowerCase());
					}
					dot.setAttribute('aria-hidden', 'true');
					button.appendChild(dot);
					button.setAttribute('aria-label', optionElement.textContent || optionElement.value);
				} else {
					button.textContent = optionElement.textContent || '';
				}

				list.appendChild(button);
			});

			const sync = () => {
				list.querySelectorAll('.sticky-mobile-option-button').forEach((buttonElement) => {
					const value = buttonElement.getAttribute('data-value');
					const matchingOption = Array.from(selectElement.options).find((option) => option.value === value);
					if (!matchingOption) return;
					buttonElement.classList.toggle('is-selected', selectElement.value === value);
					buttonElement.classList.toggle('is-disabled', !!matchingOption.disabled);
					buttonElement.setAttribute('aria-disabled', matchingOption.disabled ? 'true' : 'false');
					buttonElement.disabled = false;
				});

				if (colorOption) {
					const selectedOption = Array.from(selectElement.options).find((option) => option.value === selectElement.value);
					currentValue.textContent = selectedOption && selectedOption.value !== ''
						? ` ${selectedOption.textContent || ''}`
						: '';
				}
			};

			optionGroup.append(title, list);
			variantWrapper.appendChild(optionGroup);
			sync();
			return { sync };
		};

		sticky.querySelectorAll('select[data-sticky-option]').forEach((selectElement) => {
			const colorOption = isColorOption(selectElement);
			const wrapper = selectElement.closest('.variant-wrapper--dropdown');
			if (colorOption && wrapper) {
				wrapper.classList.add('is-sticky-color-option');
			}

			// Single-size products render the size option pre-selected server-side
			// (placeholder present but not selected, a real value chosen). Detect that
			// before we mutate the select so we can keep it selected instead of forcing
			// the customer to re-pick a size that has no alternative.
			const serverPlaceholder = selectElement.querySelector('option[value=""]');
			const serverAutoSelectedSize = !!serverPlaceholder && !serverPlaceholder.selected && selectElement.value !== '';

			if (!colorOption && !selectElement.querySelector('option[value=""]')) {
				const wrapper = selectElement.closest('.variant-wrapper--dropdown');
				const label = wrapper ? wrapper.querySelector('.variant__label') : null;
				const labelText = label ? label.textContent.trim() : 'Option';
				const placeholder = document.createElement('option');
				placeholder.value = '';
				placeholder.textContent = `Select ${labelText}`;
				placeholder.disabled = false;
				selectElement.insertBefore(placeholder, selectElement.firstChild);
			}

			if (colorOption) {
				const optionIndex = selectElement.getAttribute('data-index') || '';
				const mainVariant = getMainSelectedVariant();
				const preferredValue = (mainVariant && optionIndex && mainVariant[optionIndex]) ? mainVariant[optionIndex] : '';
				if (preferredValue) {
					const matchingOption = Array.from(selectElement.options).find((optionElement) => {
						return normalizeValue(optionElement.value) === normalizeValue(preferredValue);
					});
					if (matchingOption) {
						selectElement.value = matchingOption.value;
					}
				}

				if (!selectElement.value || selectElement.value === '') {
					const firstRealOption = Array.from(selectElement.options).find((optionElement) => optionElement.value !== '');
					if (firstRealOption) {
						selectElement.value = firstRealOption.value;
					}
				}
				return;
			}

			selectElement.value = serverAutoSelectedSize ? selectElement.value : '';
			customDropdowns.push(buildCustomDropdown(selectElement));
			const mobileGroup = buildMobileOptionGroup(selectElement);
			if (mobileGroup) mobileOptionGroups.push(mobileGroup);
		});

		const familyDropdown = sticky.querySelector('[data-sticky-family-select]');
		let familyTrigger = null;
		let syncFamilySelectionFromValue = () => {};
		if (familyDropdown) {
			familyTrigger = familyDropdown.querySelector('.sticky-select__trigger');
			const familyCurrentLabel = familyDropdown.querySelector('[data-sticky-family-current-label]');
			const familyCurrentDot = familyDropdown.querySelector('.sticky-select__value .sticky-select__dot');
			selectedFamilyColor = familyCurrentLabel ? familyCurrentLabel.getAttribute('data-sticky-family-current-value') : null;

			const familyWrapper = familyDropdown.closest('.variant-wrapper--family');
			let familyMobileButtons = [];
			let mobileFamilyCurrent = null;
			if (familyWrapper) {
				const mobileFamilyGroup = document.createElement('div');
				mobileFamilyGroup.className = 'sticky-mobile-option-group';

				const mobileFamilyTitle = document.createElement('div');
				mobileFamilyTitle.className = 'sticky-mobile-option-title';
				mobileFamilyTitle.textContent = 'Color:';
				mobileFamilyCurrent = document.createElement('span');
				mobileFamilyCurrent.className = 'sticky-mobile-option-current';
				mobileFamilyTitle.appendChild(mobileFamilyCurrent);

				const mobileFamilyList = document.createElement('div');
				mobileFamilyList.className = 'sticky-mobile-option-list';

				familyDropdown.querySelectorAll('.sticky-select__option').forEach((optionBtn) => {
					const mobileBtn = document.createElement('button');
					mobileBtn.type = 'button';
					mobileBtn.className = 'sticky-mobile-option-button sticky-mobile-option-button--color';
					mobileBtn.setAttribute('data-family-option-value', optionBtn.getAttribute('data-family-option-value') || '');
					mobileBtn.setAttribute('aria-label', optionBtn.getAttribute('data-family-color-label') || 'Color');

					const dot = document.createElement('span');
					dot.className = 'sticky-mobile-option-dot';
					const image = optionBtn.getAttribute('data-family-color-image');
					if (image) {
						dot.style.backgroundImage = `url(${image})`;
					} else {
						dot.style.setProperty('--dot-color', (optionBtn.getAttribute('data-family-color-label') || '').toLowerCase());
					}
					dot.setAttribute('aria-hidden', 'true');
					mobileBtn.appendChild(dot);

					mobileFamilyList.appendChild(mobileBtn);
					familyMobileButtons.push({ mobileBtn, optionBtn });
				});

				mobileFamilyGroup.append(mobileFamilyTitle, mobileFamilyList);
				familyWrapper.appendChild(mobileFamilyGroup);
			}

			const syncFamilyMobileButtons = () => {
				familyMobileButtons.forEach(({ mobileBtn, optionBtn }) => {
					mobileBtn.classList.toggle('is-selected', optionBtn.classList.contains('is-active'));
				});

				if (mobileFamilyCurrent) {
					const activeFamilyOption = familyDropdown.querySelector('.sticky-select__option.is-active');
					mobileFamilyCurrent.textContent = activeFamilyOption
						? ` ${activeFamilyOption.getAttribute('data-family-color-label') || ''}`
						: '';
				}
			};

			syncFamilySelectionFromValue = (colorValue) => {
				if (!colorValue) return;
				const targetOption = Array.from(familyDropdown.querySelectorAll('.sticky-select__option')).find((optionBtn) => {
					return normalizeValue(optionBtn.getAttribute('data-family-option-value')) === normalizeValue(colorValue);
				});
				if (!targetOption) return;

				familyDropdown.querySelectorAll('.sticky-select__option').forEach((btn) => btn.classList.remove('is-active'));
				targetOption.classList.add('is-active');
				selectedFamilyColor = targetOption.getAttribute('data-family-option-value') || null;

				if (familyCurrentLabel) {
					familyCurrentLabel.textContent = targetOption.getAttribute('data-family-color-label') || '';
					familyCurrentLabel.setAttribute('data-sticky-family-current-value', selectedFamilyColor || '');
				}

				if (familyCurrentDot) {
					const image = targetOption.getAttribute('data-family-color-image');
					if (image) {
						familyCurrentDot.style.backgroundImage = `url(${image})`;
						familyCurrentDot.style.backgroundSize = 'cover';
						familyCurrentDot.style.backgroundPosition = 'center';
					}
				}

				updateColorDisplay(
					targetOption.getAttribute('data-family-color-label') || '',
					targetOption.getAttribute('data-family-color-image') || null
				);

				syncFamilyMobileButtons();
			};
			syncFamilyMobileButtons();

			if (familyTrigger) {
				familyTrigger.addEventListener('click', () => {
					const nextOpen = !familyDropdown.classList.contains('is-open');
					closeCustomDropdowns();
					familyDropdown.classList.toggle('is-open', nextOpen);
					familyTrigger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
				});
			}

			familyDropdown.querySelectorAll('.sticky-select__option').forEach((optionBtn) => {
				optionBtn.addEventListener('click', () => {
					syncFamilySelectionFromValue(optionBtn.getAttribute('data-family-option-value'));

					familyDropdown.classList.remove('is-open');
					if (familyTrigger) familyTrigger.setAttribute('aria-expanded', 'false');
					applyAvailability();
					updateState();
				});
			});
		}

		const handleMobileOptionTap = (event) => {
			if (!isMobileMode()) return;
			const rawTarget = event.target;
			const eventTarget = rawTarget instanceof Element ? rawTarget : (rawTarget && rawTarget.parentElement ? rawTarget.parentElement : null);
			if (!eventTarget) return;

			const mobileBtn = eventTarget.closest('.sticky-mobile-option-button');
			if (!mobileBtn || !sticky.contains(mobileBtn)) return;

			event.preventDefault();
			event.stopPropagation();
			stickyInteractionUntil = Date.now() + 500;

			const familyValue = mobileBtn.getAttribute('data-family-option-value');
			if (familyValue) {
				syncFamilySelectionFromValue(familyValue);
				applyAvailability();
				updateState();
				return;
			}

			const optionValue = mobileBtn.getAttribute('data-sticky-mobile-option-value');
			if (!optionValue) return;

			const optionList = mobileBtn.closest('.sticky-mobile-option-list');
			if (!optionList) return;

			const selectElement = mobileListSelectMap.get(optionList);
			if (!selectElement) return;
			const optionIndex = selectElement.getAttribute('data-index') || null;

			const matchingOption = Array.from(selectElement.options).find((option) => option.value === optionValue);
			if (!matchingOption) return;

			matchingOption.disabled = false;
			selectElement.value = matchingOption.value;
			if (selectElement.value !== matchingOption.value) {
				selectElement.selectedIndex = Array.from(selectElement.options).indexOf(matchingOption);
			}

			selectElement.dispatchEvent(new Event('change'));
			syncMainOptionFromSticky(optionIndex, optionValue);

			applyAvailability();
			updateState();
		};

		sticky.addEventListener('click', handleMobileOptionTap);
		sticky.addEventListener('touchend', handleMobileOptionTap, { passive: false });

		document.addEventListener('click', (event) => {
			const clickedElement = event.target;
			const clickedStickySelect = clickedElement && clickedElement.closest
				? clickedElement.closest('.sticky-select')
				: null;

			if (!clickedStickySelect) {
				closeCustomDropdowns();
				if (familyDropdown) {
					familyDropdown.classList.remove('is-open');
					if (familyTrigger) familyTrigger.setAttribute('aria-expanded', 'false');
				}
			}
		});

		/**
		 * Synchronizes sticky controls from a concrete variant object.
		 *
		 * @param {VariantLike|null} variant
		 */
		const syncStickyFromVariant = (variant, changedOptionIndex) => {
			if (!variant) return;
			if (variant.option1) {
				syncFamilySelectionFromValue(variant.option1);
			}

			['option1', 'option2', 'option3'].forEach((optionKey) => {
				const select = sticky.querySelector(`select[data-sticky-option][data-index="${optionKey}"]`);
				if (!select) return;
				const nextValue = variant[optionKey] || '';
				if (nextValue === '' || normalizeValue(select.value) === normalizeValue(nextValue)) return;
				// Don't auto-populate size when theme.js picked it as a side-effect of a color change
				if (optionKey !== 'option1' && changedOptionIndex === 'option1') return;
				const matchingOption = Array.from(select.options).find((optionElement) => {
					return normalizeValue(optionElement.value) === normalizeValue(nextValue);
				});
				if (!matchingOption) return;
				select.value = matchingOption.value;
			});

			applyAvailability();
		};

		if (sectionRoot) {
			sectionRoot.addEventListener('variantChange', (event) => {
				if (!event.detail || !event.detail.variant) return;
				if (isMobileMode() && Date.now() < stickyInteractionUntil) return;
				latestMainVariant = event.detail.variant;
				syncStickyFromVariant(latestMainVariant, event.detail.index);
				updateState();
			});

			const mainIdInput = sectionRoot.querySelector('.product-single__form [name="id"]');
			if (mainIdInput) {
				mainIdInput.addEventListener('change', () => {
					if (isMobileMode() && Date.now() < stickyInteractionUntil) return;
					latestMainVariant = getVariantById(mainIdInput.value);
					updateState();
				});
			}
		}

		/**
		 * Reads sticky option selections (option1-3), injecting family color as
		 * option1 when selected from the family dropdown.
		 *
		 * @returns {SelectedOption[]}
		 */
		const getSelected = () => {
			const appliedFamilyColor = getAppliedFamilyColor();
			return ['option1', 'option2', 'option3'].map((optionKey, index) => {
				const optionIndex = `option${index + 1}`;
				if (optionIndex === 'option1' && appliedFamilyColor) {
					return { index: optionKey, value: appliedFamilyColor };
				}
				const select = sticky.querySelector(`select[data-sticky-option][data-index="${optionIndex}"]`);
				const selectedValue = select ? select.value : null;
				return { index: optionKey, value: selectedValue };
			});
		};

		/**
		 * Counts how many option groups must be selected to match a concrete variant.
		 *
		 * @returns {number}
		 */
		const getRequiredSelectionCount = () => {
			const required = {};
			sticky.querySelectorAll('select[data-sticky-option]').forEach((selectElement) => {
				required[selectElement.getAttribute('data-index')] = true;
			});
			if (getAppliedFamilyColor()) {
				required.option1 = true;
			}
			return Object.keys(required).length;
		};

		/**
		 * Resolves the currently selected variant for sticky desktop flow.
		 * Returns null while selection is incomplete.
		 *
		 * @returns {VariantLike|null}
		 */
		const findVariant = () => {
			const effectiveVariants = getEffectiveVariants();
			const selected = getSelected().filter((selection) => selection.value !== null && selection.value !== '');
			if (selected.length < getRequiredSelectionCount()) return null;
			return effectiveVariants.find((variant) => {
				return selected.every((selection) => normalizeValue(variant[selection.index]) === normalizeValue(selection.value));
			}) || null;
		};

		const hasMainSizeSelected = () => {
			if (!sectionRoot) return false;
			return !!sectionRoot.querySelector(
				'.variant-input-wrap input[data-variant-input]:checked:not(.variant__input--color-swatch)'
			);
		};


		const updateMobileToggleLabel = () => {
			if (!mobileToggleLabel) return;
			mobileToggleLabel.textContent = findVariant() ? txtChangeSize : txtSelectSize;
		};

		/**
		 * Updates sticky button state and hidden variant id.
		 * Mobile state is mirrored from the main PDP selection;
		 * desktop state is computed from sticky option selections.
		 */
		const updateState = () => {
			updateMobileToggleLabel();
			const variant = findVariant();
			if (idInput) idInput.value = variant ? variant.id : '';
			if (!atcBtn) return;
			if (!variant) {
				atcBtn.disabled = true;
				atcBtn.classList.remove('sold-out');
				if (atcBtnText) atcBtnText.textContent = txtAdd;
				if (atcBtnPrice) atcBtnPrice.textContent = '';
				return;
			}
			const isAvailable = !!variant.available;
			atcBtn.disabled = false;
			atcBtn.classList.toggle('sold-out', !isAvailable);
			if (atcBtnText) atcBtnText.textContent = isAvailable ? `${txtAddBag} -` : txtNotify;
			if (atcBtnPrice) atcBtnPrice.textContent = isAvailable ? formatVariantPrice(variant) : '';
		};

		/**
		 * Builds a per-option availability tree based on current partial selection.
		 * Used to disable sold-out / impossible combinations in sticky selectors.
		 *
		 * @param {SelectedOption[]} selected
		 * @returns {{
		 * 	option1: AvailabilityEntry[],
		 * 	option2: AvailabilityEntry[],
		 * 	option3: AvailabilityEntry[]
		 * }}
		 */
		const buildTree = (selected) => {
			const tree = { option1: [], option2: [], option3: [] };
			const effectiveVariants = getEffectiveVariants();
			effectiveVariants.forEach((variant) => {
				['option1', 'option2', 'option3'].forEach((optionKey) => {
					const value = variant[optionKey];
					if (!value) return;
					let entry = tree[optionKey].find((option) => option.value === value);
					if (!entry) {
						entry = { value, soldOut: true };
						tree[optionKey].push(entry);
					}
					const selected1 = selected.find((item) => item.index === 'option1');
					const selected2 = selected.find((item) => item.index === 'option2');
					const hasSelected1 = selected1 && selected1.value;
					const hasSelected2 = selected2 && selected2.value;

					if (optionKey === 'option1') {
						if (variant.available) entry.soldOut = false;
					} else if (optionKey === 'option2') {
						if (variant.available && (!hasSelected1 || variant.option1 === selected1.value)) {
							entry.soldOut = false;
						}
					} else if (
						variant.available &&
						(!hasSelected1 || variant.option1 === selected1.value) &&
						(!hasSelected2 || variant.option2 === selected2.value)
					) {
						entry.soldOut = false;
					}
				});
			});
			return tree;
		};

		/**
		 * Applies current availability tree to native select options,
		 * then synchronizes custom dropdown UI state.
		 */
		const applyAvailability = () => {
			const tree = buildTree(getSelected());
			['option1', 'option2', 'option3'].forEach((optionKey) => {
				const group = sticky.querySelector(`.variant-input-wrap[data-index="${optionKey}"]`);
				if (!group) return;
				const select = group.querySelector('select[data-sticky-option]');
				if (!select) return;
				const selectedValueBeforeAvailability = select.value;

				Array.from(select.options).forEach((optionElement) => {
					optionElement.disabled = optionElement.value !== '';
				});

				tree[optionKey].forEach((optionState) => {
					if (optionState.soldOut) return;
					Array.from(select.options).forEach((optionElement) => {
						if (optionElement.value === optionState.value) {
							optionElement.disabled = false;
						}
					});
				});

				if (selectedValueBeforeAvailability) {
					const selectedOption = Array.from(select.options).find((optionElement) => optionElement.value === selectedValueBeforeAvailability);
					if (selectedOption) {
						selectedOption.disabled = false;
						select.value = selectedValueBeforeAvailability;
					}
				}
			});
			customDropdowns.forEach(({ sync }) => sync());
			mobileOptionGroups.forEach(({ sync }) => sync());
		};

		sticky.querySelectorAll('select[data-sticky-option]').forEach((selectElement) => {
			selectElement.addEventListener('change', () => {
				applyAvailability();
				updateState();
			});
		});

		if (stickyForm) {
			stickyForm.addEventListener('submit', (event) => {
				event.preventDefault();
				if (!findVariant()) {
					updateState();
					return;
				}
				if (!idInput || !idInput.value) {
					updateState();
					return;
				}

				// If variant is sold out, open Back in Stock popup instead of adding to cart
				const bisVariantId = String(idInput.value);
				const bisVariant = variants.find((v) => String(v.id) === bisVariantId)
					|| familyVariants.find((v) => String(v.id) === bisVariantId);
				if (bisVariant && !bisVariant.available && window.BIS && BIS.popup) {
					BIS.popup.show({ variantId: Number(bisVariantId) });
					return;
				}

				if (atcBtn) atcBtn.classList.add('btn--loading');
				const cartUrl = (window.theme && theme.routes && theme.routes.cartAdd)
					? theme.routes.cartAdd
					: '/cart/add.js';

				fetch(cartUrl, {
					method: 'POST',
					body: new FormData(stickyForm),
					headers: { 'X-Requested-With': 'XMLHttpRequest' }
				})
					.then((response) => response.json())
					.then((data) => {
						if (atcBtn) atcBtn.classList.remove('btn--loading');
						stickyForm.dispatchEvent(new CustomEvent('ajaxProduct:added', {
							detail: { product: data, addToCartBtn: atcBtn },
							bubbles: true
						}));
						document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
					})
					.catch(() => {
						if (atcBtn) atcBtn.classList.remove('btn--loading');
					});
			});
		}

		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => {
				if (!isMobileMode()) return;
				setMobileExpanded(false);
				closeCustomDropdowns();
				updateState();
			});
		}

		if (mobileToggleBtn) {
			mobileToggleBtn.addEventListener('click', () => {
				if (!isMobileMode()) return;
				const willExpand = !isMobileExpanded();
				setMobileExpanded(willExpand);
				mobileToggleBtn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
				if (!willExpand) closeCustomDropdowns();
				updateState();
			});
		}

		sticky.querySelectorAll('[data-scroll-to-swatches]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const swatches = document.querySelector('.product-family__wrapper');
				if (swatches) {
					swatches.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				if (isMobileMode()) {
					setMobileExpanded(false);
					updateState();
				}
			});
		});

		const toggleBtn = sticky.querySelector('.product-add-to-cart-sticky--inner');
		if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');

		/**
		 * Controls sticky visibility:
		 * - mobile: appears once main add-to-cart button scrolls out of view
		 * - desktop: appears once main add-to-cart button scrolls out of view
		 */
		const updateVisibility = () => {
			if (isMobileMode()) {
				// On mobile, hide only when footer overlaps the sticky bar itself
				if (footerEl && footerEl.getBoundingClientRect().top < window.innerHeight - sticky.offsetHeight) {
					sticky.classList.remove('is-visible');
					return;
				}
				if (!mainAtcBtn) {
					sticky.classList.remove('is-visible');
					return;
				}

				const rect = mainAtcBtn.getBoundingClientRect();
				const showSticky = rect.bottom < 0;
				sticky.classList.toggle('is-visible', showSticky);
				return;
			}

			if (footerEl && footerEl.getBoundingClientRect().top < window.innerHeight - sticky.offsetHeight) {
				sticky.classList.remove('is-visible');
				return;
			}

			if (!mainAtcBtn) {
				sticky.classList.add('is-visible');
				return;
			}

			const rect = mainAtcBtn.getBoundingClientRect();
			const isVisible = rect.bottom <= 104;
			sticky.classList.toggle('is-visible', isVisible);
		};

		// On init, sync color only — user must pick size themselves
		// (except single-size products, whose size is pre-selected above).
		const _initVariant = getMainSelectedVariant();
		if (_initVariant && _initVariant.option1) {
			syncFamilySelectionFromValue(_initVariant.option1);
		}
		applyAvailability();
		updateState();
		window.addEventListener('scroll', updateVisibility, { passive: true });
		window.addEventListener('resize', () => {
			if (!isMobileMode()) {
				setMobileExpanded(false);
			}
			updateVisibility();
			updateState();
		});
		updateVisibility();
	};

	document.addEventListener('DOMContentLoaded', () => {
		stickyRoots.forEach((sticky) => {
			initSticky(sticky);
		});
	});
})();
